/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Single source of every user-visible string in the booking wizard (05 §5), so
 * the whole file goes to QA label control as one reviewable unit. Consent and
 * questionnaire prose are intentionally placeholder keys (O4/O5 — QA-owned);
 * never hardcode legal or clinical copy elsewhere. Interpolated strings are
 * functions so the copy stays in this file.
 *
 * Copy rules applied (D5): no "HIPAA notice" label; no cardiac/diagnosis language
 * on patient-facing framing. Exception: consent.document is the Medaica Consent
 * Form VERBATIM from legal (informed consent must state limitations) — never
 * reword it here; changes come from legal with a CONSENT_POLICY_VERSION bump.
 */
type ConsentDocSection = {
  heading: string | null
  paragraphs?: string[]
  bullets?: string[]
  after?: string[]
}

const bookingCopy = {
  appName: "scheduling",
  supportEmail: "support@medaica.com",

  verify: {
    title: "Let's verify it's you",
    subtitle: (maskedEmail: string): string =>
      `We've emailed a 6-digit code to ${maskedEmail}. Enter it below to open your exams.`,
    neutralSubtitle: "If you have exams with us, we've emailed a 6-digit code. Enter it below to continue.",
    continueButton: "Continue",
    didntGetIt: "Didn't get it?",
    resend: "Request a new code",
    codeExpires: "Code expires in 10 minutes",
    // Deliberately non-committal: the resend response is neutral (200 even when rate-limited /
    // throttled), so the client can't truthfully promise a NEW code was sent.
    resent: "Check your email for the latest code — it can take a minute to arrive.",
    spamHint: "Can't find it? Check your spam folder.",
    personalNote: "This link is personal to your order — please don't forward it.",
    // Shown after several failed attempts — actionable and non-enumerating (the client can't be
    // told it's on a decoy/rate-limited session, so this guides any stuck user the same way).
    stuckHelp:
      "Still not working? Use the code from your most recent email. If it keeps failing, " +
      "wait a minute before requesting a new one, or email",
    // Shown after repeated resends — code sends are capped per hour, so this sets the wait
    // expectation without claiming (or denying) a rate limit.
    resendLimitHelp:
      "Requested a few codes already? There can be a short wait before another can be sent — " +
      "check your most recent email, or email",
    errors: {
      wrongCode: (attemptsRemaining: number): string =>
        `That code isn't right. ${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} left.`,
      wrongCodeGeneric: "That code isn't right — please try again.",
      locked: "Too many attempts. Please contact support@medaica.com.",
      expired: "That code has expired — request a new one.",
      sendFailed: "We couldn't send the code — please try again.",
      generic: "Something went wrong. Please try again.",
    },
  },

  account: {
    loadError: "We couldn't load your exams. Please try again.",
    retry: "Retry",
    greeting: (firstName: string): string => `Hi ${firstName},`,
    examsAvailable: (count: number, validThrough: string | null): string => {
      const s = count === 1 ? "" : "s"
      // Expiry is only meaningful when the pool is non-empty — never "0 available (valid through …)".
      return validThrough && count > 0
        ? `You have ${count} exam${s} available (valid through ${validThrough}).`
        : `You have ${count} exam${s} available.`
    },
    schedule: "Schedule an exam",
    noCredits: "You've used all your exams — purchase more to schedule another.",
    bookedHeading: "Exam history",
    noBookings: "No exams scheduled yet.",
    columns: { who: "Who", when: "When", confirmationId: "Confirmation ID", status: "Status" },
    joinLink: "Join link",
    supportPrompt: "Need to reschedule or cancel an exam?",
    supportCta: "Email support@medaica.com",
    statusLabels: {
      scheduled: "Scheduled",
      completed: "Completed",
      no_show: "No-show",
      cancelled: "Cancelled",
    },
  },

  stepper: { patient: "1 · Patient", consent: "2 · Consent", health: "3 · Health", time: "4 · Time" },
  bookingFor: (name: string): string => `Scheduling for ${name}`,
  examsAvailable: (count: number): string => `${count} exam${count === 1 ? "" : "s"} available`,

  patient: {
    // Restore when "Someone else" booking is re-enabled (see ALLOW_BOOKING_FOR_OTHERS in patient.tsx):
    //   title: "Who is this exam for?"
    //   subtitle: "It can be you, or someone you care for — a parent, partner, anyone."
    title: "Your details",
    subtitle: "Please review and complete your information below.",
    me: (name: string): string => `Me (${name})`,
    someoneElse: "Someone else",
    firstName: "First name",
    lastName: "Last name",
    dateOfBirth: "Date of birth",
    gender: "Sex",
    // Shared empty-state placeholder for the DOB + Sex picker triggers.
    selectPlaceholder: "Select…",
    // "Prefer not to say" sends the value "unknown" (backend Sex.UNKNOWN). Until the backend adds
    // that enum value, an unmapped sex harmlessly stores as null — so this is safe to ship first.
    genderOptions: { male: "Male", female: "Female", unknown: "Prefer not to say" },
    email: "Email",
    emailHelp: "Their confirmation and join link arrive here.",
    phone: "Phone",
    phoneHelp: "In case we need to reach you on exam day.",
    emailNote:
      "Each patient needs their own email address — that's where their confirmation, join link and results arrive.",
    back: "Back",
    continueButton: "Continue",
    errors: {
      required: "Required",
      email: "Enter a valid email address",
      phone: "Enter a valid phone number",
      underage: "Exams are for adults 18+ at this time.",
    },
  },

  // Consent + questionnaire framing are O5/O4 placeholders pending QA label control (D5 wording applied).
  consent: {
    title: "Consent",
    subtitleForSelf: "Please review and confirm.",
    subtitleForOther: (name: string): string =>
      `Please review and confirm on ${name}'s behalf (or have them do it with you).`,
    formTitle: "Medaica Consent Form.",
    formBody:
      "I have read and agree to the Medaica Consent Form — what the exam is, how it works, " +
      "its benefits, limitations and risks, and my choices.",
    viewFullForm: "View full form",
    hideFullForm: "Hide full form",
    // Public/printable copy of the same document (Shopify page). The wizard renders the
    // canonical, version-frozen text from `document` below — this link is a secondary
    // reference, never the source (the page is CMS-editable with no version bump).
    fullFormUrl: "https://medaica.com/pages/telehealth-informed-consent",
    fullFormAlso: "Also available in printable form at",
    documentTitle: "Medaica Consent Form",
    // VERBATIM legal text (policy v1) — see the file-header exception. Consumed by the
    // consent step's "View full form" panel; the consent row's policyVersion freezes it.
    document: [
      {
        heading: null,
        paragraphs: [
          "Medaica offers virtual Heart Exams that allow heart sounds to be collected remotely " +
            "using the Medaica device and made available for review by a qualified clinician.",
          "Please take a moment to read the information below before agreeing to proceed with your exam.",
        ],
      },
      {
        heading: "What Is a Medaica Heart Exam?",
        paragraphs: [
          "A Medaica Heart Exam is a screening exam that allows heart sounds to be collected " +
            "remotely using an FDA-cleared digital medical device and made available for review " +
            "by a qualified clinician.",
          "The Heart Exam is intended to provide information that can help identify whether " +
            "further medical evaluation may be appropriate. It is not, by itself, a diagnostic " +
            "test and does not diagnose or rule out a heart condition.",
          "During the exam, a Medaica Guide helps you use the device and obtain the recordings " +
            "needed for subsequent clinical review.",
          "Your Medaica Guide does not assess, diagnose, interpret your recordings, or provide " +
            "medical advice during the exam.",
        ],
      },
      {
        heading: "How Your Heart Exam Works",
        paragraphs: [
          "During your exam, a Medaica Guide will connect with you remotely and guide you " +
            "through the collection of heart sounds.",
          "Your Medaica Guide may:",
        ],
        bullets: [
          "Guide you through positioning and using the Medaica device",
          "Monitor the collection process to help ensure usable recordings are obtained",
          "Ask you to repeat a recording when needed",
          "Answer questions about the exam process",
        ],
        after: [
          "Following the exam, the heart sounds and other exam data collected during your " +
            "session will be made available for review by a qualified clinician.",
        ],
      },
      {
        heading: "Benefits of a Medaica Heart Exam",
        paragraphs: ["A Medaica Heart Exam may provide:"],
        bullets: [
          "Convenient collection of heart sounds from home",
          "No travel or waiting room for the sound collection process",
          "Timely collection of heart sound recordings for clinical review",
          "Information that may help identify whether further medical evaluation or testing is appropriate",
        ],
      },
      {
        heading: "Limitations and Risks",
        paragraphs: ["A Medaica Heart Exam may not be appropriate for every situation. Limitations may include:"],
        bullets: [
          "Difficulty obtaining usable recordings due to factors such as device placement, " +
            "movement, background noise, individual anatomy or body composition, connectivity, " +
            "or other technical issues",
          "The need to repeat part of the exam if adequate recordings are not obtained",
          "Situations where an in-person examination, additional testing, or other medical evaluation is needed",
          "The Medaica Heart Exam is a screening exam and is not, by itself, diagnostic. A " +
            "normal or reassuring finding does not rule out an underlying heart condition",
        ],
        after: [
          "The Medaica Heart Exam is not intended for emergency use and should not be relied " +
            "upon to diagnose or rule out a medical emergency.",
          "If you believe you are experiencing a medical emergency, call 911 or your local " +
            "emergency services immediately.",
        ],
      },
      {
        heading: "Use of the Medaica Device",
        paragraphs: [
          "The Medaica device is used to capture heart sounds for review by a qualified healthcare professional.",
          "By using the device, you understand that:",
        ],
        bullets: [
          "You should follow the guidance provided by your Medaica Guide during the exam",
          "The quality of the recordings may be affected by positioning, movement, background " +
            "noise, individual anatomy or body composition, connectivity, or other technical factors",
          "You may be asked to repeat part of the exam if adequate recordings cannot be obtained",
          "The Heart Exam does not replace an in-person examination, diagnostic testing, or " +
            "other medical evaluation that your healthcare provider may consider appropriate",
        ],
      },
      {
        heading: "Your Privacy",
        paragraphs: [
          "Your health information, including heart sound recordings and related exam data, is " +
            "handled in accordance with applicable law and Medaica’s Privacy Policy.",
          "Additional privacy and security information is available on our website.",
        ],
      },
      {
        heading: "Your Choice",
        paragraphs: ["Participation in a Medaica Heart Exam is optional.", "You may:"],
        bullets: [
          "Ask questions about the exam process at any time",
          "Choose not to proceed",
          "Stop the exam at any point",
          "Seek in-person medical care or other medical evaluation at any time",
        ],
      },
      {
        heading: "Consent",
        paragraphs: ["By selecting “I Agree” and proceeding with your Medaica Heart Exam, you acknowledge that:"],
        bullets: [
          "You understand what a Medaica Heart Exam is and how it works",
          "You understand that the Heart Exam is a screening exam and is not, by itself, diagnostic",
          "You understand the role of the Medaica Guide and that the Guide does not assess, " +
            "diagnose, interpret your recordings, or provide medical advice",
          "You have had the opportunity to review this information and ask questions",
          "You understand the benefits, limitations, and risks described above",
          "You consent to the collection of your heart sounds and related exam data",
          "You consent to those recordings and related exam data being made available for " +
            "subsequent review by a qualified clinician",
          "You understand that you may stop the exam at any time",
        ],
      },
      {
        heading: "Questions?",
        paragraphs: ["If you have questions before your exam, please contact support@medaica.com."],
      },
    ] as ConsentDocSection[],
    privacyTitle: "Privacy and data processing.",
    privacyBody:
      "I agree that Medaica may collect, use, and process the patient's health information to " +
      "provide the exam and clinician-reviewed report, as described in the Privacy Policy and " +
      "Consumer Health Data Notice.",
    emailsTitle: "Appointment communications.",
    emailsBody: (email: string): string =>
      `Send the appointment confirmation, exam link, and results notification to ${email}.`,
    footnote:
      "Consent will be recorded with a timestamp for this patient and exam. Consent may be " +
      "withdrawn at any time by contacting support@medaica.com, although withdrawal will not " +
      "affect processing already completed.",
    back: "Back",
    agree: "Agree & continue",
  },

  health: {
    title: "A few health questions",
    subtitle: "This helps the care team prepare for the exam. It takes about 3 minutes.",
    continueButton: "Continue",
    back: "Back",
    completePrompt: "Please answer all questions to continue.",
    unavailable: "The questionnaire isn't available right now — you can complete it during the exam.",
    optionalPlaceholder: "Optional…",
    // Global safety notice (not from the questionnaire API) — rendered below the questions. Split so
    // "Important" can be emphasized in the callout; the rendered text stays identical (health.tsx).
    emergencyNoticeLabel: "Important",
    emergencyNotice:
      "This questionnaire and the Medaica At-Home Heart Exam are not intended for " +
      "medical emergencies. If you are currently experiencing a medical emergency, call 911 or " +
      "local emergency services immediately.",
  },

  time: {
    title: "Pick a time",
    // Timezone info alert (left) + the change dropdown (right) above the slot card.
    timesShownIn: "Times shown in",
    autoDetectedNote: "Auto-detected from your device. If this isn't correct, change it on the right.",
    changeTimeZone: "Change time zone?",
    // Built from GET /booking/config (single source of truth) — no hardcoded durations. The
    // lead renders as days when the hours divide evenly by 24 (120 → "5 days"), else hours.
    examDuration: (examMinutes: number, horizonWeeks: number, minLeadHours: number): string => {
      const plural = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? "" : "s"}`
      const earliest = minLeadHours % 24 === 0 ? plural(minLeadHours / 24, "day") : plural(minLeadHours, "hour")
      const weeks = plural(horizonWeeks, "week")
      return (
        `The exam takes ${examMinutes} minutes. ` +
        `You can schedule up to ${weeks} out (earliest: ${earliest} from now).`
      )
    },
    // Week pager — explicitly "week"-labeled inline links so they can't be mistaken for the
    // flow's Back button (stakeholder feedback).
    earlier: "‹ Earlier week",
    later: "Next Week ›",
    loadError: "We couldn't load available times. Please try again.",
    retry: "Retry",
    confirm: (label: string): string => `Confirm ${label}`,
    confirmDisabled: "Select a time",
    back: "Back",
    slotTaken: "That time just filled — pick another.",
    noCredits: "You're out of exams.",
    genericError: "Something went wrong scheduling that time. Please try again.",
    noSlots: "No times available in this window.",
  },

  emailEntry: {
    title: "Enter your email to access your exams",
    subtitle: "We'll email you a 6-digit code to continue.",
    emailLabel: "Email address",
    submit: "Continue",
    neutralNote: "If you have exams with us, we'll email you a code.",
    error: "We couldn't send the code — please try again.",
  },

  done: {
    scheduledFor: "You are scheduled for",
    selectedTimeZone: "Selected time zone:",
    confirmationId: "Confirmation ID",
    // Split around the email so the address can be bolded in place (done.tsx).
    joinNoteBefore: "Your join link is in the confirmation email sent to ",
    joinNoteAfter: ". Please check your spam or junk folders if it doesn't arrive.",
    faqPrompt: "Questions about your Medaica Home Heart Exam? See our tips and FAQs",
    faqLinkText: "here",
    faqUrl: "https://medaica.com/pages/faq",
    // Email is a mailto link mid-sentence, so the line is split around it.
    rescheduleBefore: "Reschedule / cancel — email ",
    rescheduleAfter: " with your Confirmation ID.",
    bookAnother: (remaining: number): string => `Schedule another exam — ${remaining} left`,
    viewMyExams: "View my exams",
  },
}

export default bookingCopy
